
import { type ClassValue, clsx } from "clsx"
import { twMerge } from "tailwind-merge"

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

/**
 * Converts a HEX color string to an HSL object.
 * @param hex The HEX color string (e.g., "#RRGGBB").
 * @returns An object { h, s, l } or null if conversion fails.
 */
export function hexToHsl(hexString: string): { h: number; s: number; l: number } | null {
  if (!hexString || !hexString.match(/^#[0-9A-Fa-f]{6}$/)) {
    // console.warn(`Invalid HEX color provided to hexToHsl: ${hexString}`);
    return null;
  }

  let r_val = parseInt(hexString.slice(1, 3), 16);
  let g_val = parseInt(hexString.slice(3, 5), 16);
  let b_val = parseInt(hexString.slice(5, 7), 16);

  r_val /= 255; g_val /= 255; b_val /= 255;
  const max = Math.max(r_val, g_val, b_val);
  const min = Math.min(r_val, g_val, b_val);
  let h = 0, s = 0; // Explicitly initialize h and s
  const l = (max + min) / 2;

  if (max === min) {
    h = s = 0; // achromatic
  } else {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r_val: h = (g_val - b_val) / d + (g_val < b_val ? 6 : 0); break;
      case g_val: h = (b_val - r_val) / d + 2; break;
      case b_val: h = (r_val - g_val) / d + 4; break;
    }
    h /= 6;
  }

  return {
    h: Math.round(h * 360),
    s: Math.round(s * 100),
    l: Math.round(l * 100),
  };
}

/**
 * Converts an HSL object to an HSL string for CSS variables.
 * @param hsl An object { h, s, l }.
 * @returns A string in the format "H S% L%".
 */
export function hslToString(hsl: { h: number; s: number; l: number }): string {
  return `${hsl.h} ${hsl.s}% ${hsl.l}%`;
}

/**
 * Checks if a given URL string is potentially valid for an image src.
 * Allows relative paths starting with '/', data URIs, and http/https URLs.
 * It also checks if the URL is not the literal string "string" or a placeholder.
 * @param url The URL string to validate.
 * @returns True if the URL is potentially valid and NOT a placeholder, false otherwise.
 */
export function isValidImageUrl(url: string | undefined | null): boolean {
  if (!url || typeof url !== 'string' || url.trim() === '' || url.toLowerCase() === 'string') {
    return false;
  }
  
  // Desconsidera placeholders como válidos para forçar o uso de imagens reais ou fallback de cores
  const lowerUrl = url.toLowerCase();
  const placeholderDomains = [
    'placehold.co', 
    'placehold.it', 
    'picsum.photos', 
    'via.placeholder.com', 
    'placeholder.com'
  ];
  
  if (placeholderDomains.some(domain => lowerUrl.includes(domain))) {
      return false;
  }

  return url.startsWith('/') || url.startsWith('data:') || url.startsWith('http:') || url.startsWith('https:');
}

/**
 * Resizes an image file client-side OR a Buffer server-side.
 * @param source The image source (File for client, Buffer for server).
 * @param maxSize The maximum width or height of the resized image.
 * @param outputFormat The desired output format (e.g., 'image/jpeg', 'image/png').
 * @returns A Promise that resolves with the resized image as a File (client) or Buffer (server).
 */
export async function resizeImage<T extends File | Buffer>(
  source: T,
  maxSize: number,
  outputFormat?: 'image/jpeg' | 'image/png'
): Promise<T> {
  // Determine format: use provided, or detect from source, or default to jpeg
  let finalFormat: 'image/jpeg' | 'image/png' = 'image/jpeg';
  
  if (outputFormat) {
    finalFormat = outputFormat;
  } else if (typeof window !== 'undefined' && source instanceof File) {
    finalFormat = source.type === 'image/png' ? 'image/png' : 'image/jpeg';
  }

  // Client-side (browser) execution
  if (typeof window !== 'undefined' && source instanceof File) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (event) => {
        if (!event.target?.result) return reject(new Error("FileReader did not return a result."));
        const img = new Image();
        img.onload = () => {
          const canvas = document.createElement('canvas');
          let { width, height } = img;
          if (width > height) {
            if (width > maxSize) { height = Math.round(height * (maxSize / width)); width = maxSize; }
          } else {
            if (height > maxSize) { width = Math.round(width * (maxSize / height)); height = maxSize; }
          }
          canvas.width = width;
          canvas.height = height;
          const ctx = canvas.getContext('2d');
          if (!ctx) return reject(new Error('Could not get canvas context'));
          ctx.drawImage(img, 0, 0, width, height);
          canvas.toBlob((blob) => {
            if (!blob) return reject(new Error('Canvas to Blob conversion failed'));
            resolve(new File([blob], (source as File).name, { type: finalFormat, lastModified: Date.now() }) as T);
          }, finalFormat, 0.92);
        };
        img.onerror = (err) => reject(err);
        img.src = event.target.result as string;
      };
      reader.onerror = (err) => reject(err);
      reader.readAsDataURL(source);
    });
  } 
  // Server-side (Node.js) execution
  else if (typeof window === 'undefined' && source instanceof Buffer) {
      try {
        const sharp = (await import('sharp')).default;
        // Simple buffer type check for PNG
        if (!outputFormat && source[0] === 0x89 && source[1] === 0x50) {
            finalFormat = 'image/png';
        }
        const format = finalFormat.split('/')[1] as 'jpeg' | 'png';
        return sharp(source)
            .resize({ width: maxSize, height: maxSize, fit: 'inside', withoutEnlargement: true })
            .toFormat(format, { quality: 90 })
            .toBuffer() as Promise<T>;
      } catch (error) {
         console.error("Erro ao tentar usar a biblioteca 'sharp' no servidor.", error);
         return Promise.resolve(source);
      }
  }

  throw new Error("Unsupported source type for resizeImage.");
}


/**
 * Converts a data URI string into a Blob object.
 */
function dataURItoBlob(dataURI: string): Blob {
  const byteString = atob(dataURI.split(',')[1]);
  const mimeString = dataURI.split(',')[0].split(':')[1].split(';')[0];
  const ab = new ArrayBuffer(byteString.length);
  const ia = new Uint8Array(ab);
  for (let i = 0; i < byteString.length; i++) {
    ia[i] = byteString.charCodeAt(i);
  }
  return new Blob([ab], { type: mimeString });
}


/**
 * Converts a data URI string into a File object.
 * @param dataURI The data URI to convert.
 * @param filename The desired filename for the new File object.
 * @returns A File object.
 */
export function dataURItoFile(dataURI: string, filename: string): File {
    const blob = dataURItoBlob(dataURI);
    return new File([blob], filename, { type: blob.type });
}

/**
 * This helper function ensures that any data fetched on the server,
 * especially data containing Firestore Timestamps, is safe to pass to Client Components.
 * It recursively traverses objects and arrays.
 */
export const serializeData = (data: any): any => {
    if (data === null || data === undefined) {
      return data;
    }
  
    // Firestore Timestamps (both client and admin SDK duck-typing)
    // The check `typeof data.toDate === 'function'` is a safe way to identify Timestamp objects
    // from both 'firebase/firestore' and 'firebase-admin/firestore'.
    if (typeof data === 'object' && typeof data.toDate === 'function') {
      return data.toDate().toISOString();
    }
  
    // Arrays
    if (Array.isArray(data)) {
      return data.map(serializeData);
    }
  
    // Plain objects
    if (typeof data === 'object' && Object.prototype.toString.call(data) === '[object Object]') {
      const serializedObject: { [key: string]: any } = {};
      for (const key in data) {
        if (Object.prototype.hasOwnProperty.call(data, key)) {
          serializedObject[key] = serializeData(data[key]);
        }
      }
      return serializedObject;
    }
  
    // Other primitives
    return data;
};
